/**
 * @file EditorPage.js
 * @description The main React component for the AI-powered rich text editor.
 * It manages the EditorJS instance, WebSocket connection to the AI copilot,
 * and all related UI state, including a real-time AI activity log.
 */

import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import * as api from "../../../api/apiConnector.js";
import UserContext from "../../../components/User_Context.js";
import * as uploadManager from "../../../components/ui/fileupload.js";

// --- EditorJS and Tools ---
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import Marker from "@editorjs/marker";
import Checklist from "@editorjs/checklist";
import Code from "@editorjs/code";
import Table from "@editorjs/table";

// --- DOCX Exporter ---
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
} from "docx";
import { saveAs } from "file-saver";

// --- Components and Styling ---
import "./EditorPage.css";
import EditorToolbar from "../components/EditorToolbar";

// A constant to define the custom MIME type for Editor.js JSON data
const EDITOR_JS_MIME_TYPE = "application/x.editorjs+json;charset=utf-8";

// A constant for the max number of messages to keep in the activity log
const MAX_ACTIVITY_LOG = 20;

/**
 * The main Editor Page component.
 */
const EditorPage = () => {
  // --- State Hooks ---

  // Router and User Context
  const [searchParams] = useSearchParams();
  const { currentUser } = useContext(UserContext);

  // URL parameters for document context
  const type = searchParams.get('type'); // Document type
  const id = searchParams.get('id');     // Document ID (fileId)
  const groundingId = searchParams.get('groundingId'); // Grounding folder ID from URL

  // Editor Instance
  const [editor, setEditor] = useState(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const editorHolderRef = useRef(null); // DOM element for EditorJS
  const editorInstanceRef = useRef(null); // Mutable ref to hold the EditorJS instance

  // WebSocket and AI State
  const [socket, setSocket] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [aiStatus, setAiStatus] = useState("Connecting..."); // Primary status message (e.g., "Ready")
  const [aiClarification, setAiClarification] = useState(null); // { question, options }
  const isAiProcessing = useRef(false); // Ref to track if AI is busy

  // Real-time feedback log for AI thoughts and status
  const [agentActivity, setAgentActivity] = useState([]);

  // History (Chat & Document)
  const [conversationHistory, setConversationHistory] = useState([]);
  const [documentHistory, setDocumentHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // UI State
  const [copyButtonText, setCopyButtonText] = useState("Copy Text");
  const [fontFamily, setFontFamily] = useState("Calibri");
  const [fontSize, setFontSize] = useState("16");
  const [showChat, setShowChat] = useState(false);
  const [fileData, setFileData] = useState(null); // Metadata of the loaded file

  // --- NEW: State for maximizing the chat panel ---
  const [isChatMaximized, setIsChatMaximized] = useState(false);

  // Grounding Modal State
  const [showGroundingModal, setShowGroundingModal] = useState(false);
  const [groundingFolders, setGroundingFolders] = useState([]);
  const [selectedGroundingIds, setSelectedGroundingIds] = useState([]);
  const [loadingGroundingFolders, setLoadingGroundingFolders] = useState(false);

  // --- Utility Functions ---

  /**
   * Saves the current editor data to the undo/redo history stack.
   * This function truncates any "future" history if a change is made after an undo.
   * @param {Object} dataToSave - The EditorJS data object to save.
   */
  const saveToHistory = useCallback(
    async (dataToSave) => {
      if (!dataToSave) return;
      setDocumentHistory((prev) => {
        // Truncate history after the current index if new changes are made
        const newHistory = prev.slice(0, historyIndex + 1);
        return [...newHistory, dataToSave];
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex]
  );

  /**
   * Creates a standardized JSON string for WebSocket messages.
   * It injects the necessary context (file/folder IDs) into every message.
   * @param {string} messageType - The type of message (e.g., 'processRequest').
   * @param {Object} payload - The main payload for the message.
   * @returns {string} A JSON-stringified message.
   */
  const createWebSocketMessage = useCallback((messageType, payload = null) => {
    // Use manually selected grounding IDs first, fall back to URL param
    const effectiveGroundingId = selectedGroundingIds.length > 0
      ? selectedGroundingIds.join(',')
      : groundingId;

    const message = {
      type: messageType,
      urlParams: {
        type: type, // The document type
        fileId: id, // The specific file ID (can be null)
        folderId: null, // Placeholder for folderId if you add it
        groundingFolderId: effectiveGroundingId // The context folder ID
      },
      payload: payload
    };

    return JSON.stringify(message);
  }, [type, id, groundingId, selectedGroundingIds]);

  /**
   * Utility to read a Blob object as plain text.
   * @param {Blob} blob - The blob to read.
   * @returns {Promise<string>} Text content of the blob.
   */
  function readBlobAsText(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(blob);
    });
  }

  /**
   * Adds a new message to the agent activity log.
   * This provides real-time feedback to the user.
   * @param {Object} payload - The status or reasoning payload from the backend.
   * @param {'status' | 'reasoning'} type - The type of message.
   */
  const addAgentActivityMessage = (payload, type = 'status') => {
    const newMessage = {
      type: type, // 'status' or 'reasoning'
      message: payload.message || payload.thought,
      detail: payload.detail || null,
      stage: payload.stage || null,
      timestamp: new Date().toISOString(),
    };

    setAgentActivity(prev => {
      const newActivity = [...prev, newMessage];
      // Prune the log to avoid overwhelming the UI
      if (newActivity.length > MAX_ACTIVITY_LOG) {
        return newActivity.slice(newActivity.length - MAX_ACTIVITY_LOG);
      }
      return newActivity;
    });
  };

  // --- Effects ---

  /**
   * Main Initialization Effect (useEffect).
   * This runs once on component mount to:
   * 1. Load the document content from the server if an 'id' is present.
   * 2. Initialize the EditorJS instance.
   * 3. Establish the WebSocket connection.
   */
  useEffect(() => {
    // This async function holds the logic
    const initializeEditorAndSocket = async () => {
      let documentData = null;
      // --- 1. Load Existing Document ---
      if (id) {
        try {
          const itemResult = await api.sendMessage("dms/getFileInfo", { user: currentUser(), body: { id } });
          setFileData(itemResult);

          const fileBlob = await api.downloadFileGet(`dms/wopi/content?id=${id}`, { user: currentUser(), body: {} });

          if (fileBlob && fileBlob.size > 0 && (fileBlob.type === EDITOR_JS_MIME_TYPE || fileBlob.type === 'editorjs/json')) {
            const fileContentText = await readBlobAsText(fileBlob);
            documentData = JSON.parse(fileContentText);
          }
        } catch (error) {
          console.error("Failed to load document:", error);
          setAiStatus("Error loading document.");
        }
      }

      // --- 2. Initialize EditorJS ---
      if (!editorInstanceRef.current && editorHolderRef.current) {
        const editorInstance = new EditorJS({
          holder: editorHolderRef.current,
          data: documentData || { time: new Date().getTime(), blocks: [], version: "2.29.1" },
          tools: {
            header: Header,
            list: List,
            quote: Quote,
            marker: Marker,
            checklist: Checklist,
            code: Code,
            table: Table,
          },
          placeholder: "Start writing or use the AI assistant...",
          onReady: async () => {
            console.log("Editor.js is ready.");
            setIsEditorReady(true);
            const initialSaveData = await editorInstance.save();
            setDocumentHistory([initialSaveData]);
            setHistoryIndex(0);
          },
        });

        editorInstanceRef.current = editorInstance;
        setEditor(editorInstance);
      }

      // --- 3. Establish WebSocket Connection ---
      try {
        const apiUrl = `${process.env.REACT_APP_AIEDITOR_API_URL}/copilot/`;
        const ws = new WebSocket(apiUrl);
        setSocket(ws); // Save the socket to state

        ws.onopen = () => setAiStatus("Ready");
        ws.onclose = () => setAiStatus("Disconnected");
        ws.onerror = () => setAiStatus("Connection Error");
      } catch (error) {
        console.error("WebSocket connection failed:", error);
        setAiStatus("Connection Error");
      }
    };

    // Call the async init function
    initializeEditorAndSocket();

    // --- 4. Cleanup Function ---
    return () => {
      // Use the ref for cleanup to get the most current instance
      if (
        editorInstanceRef.current &&
        typeof editorInstanceRef.current.destroy === "function"
      ) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }

      // We don't use `socket` from state here, as it might be stale.
      // Instead, we should store `ws` in a ref if we need to close it.
      // For now, we'll assume the browser handles the close.
      // A better pattern would be to store `ws` in a ref.
    };
    // The empty dependency array [] ensures this effect runs only once on mount.
  }, []);

  /**
   * Applies an AI-generated patch to the editor content.
   * Saves a new state to the document history after *each* patch.
   * @param {Object} patch - The patch object from the backend.
   */
  const applyPatch = useCallback(
    async (patch) => {
      if (!isAiProcessing.current) return;
      if (!editor || !editor.isReady) return;

      const { operation, blockId, updatedBlock, newBlock, referenceId } = patch;

      try {
        if (operation === "add") {
          const targetIndex = referenceId
            ? editor.blocks.getBlockIndex(referenceId) + 1
            : (editor.blocks.getBlocksCount() > 0 ? editor.blocks.getBlocksCount() : 0); // Fallback
          await editor.blocks.insert(
            newBlock.type,
            newBlock.data,
            {},
            targetIndex,
            true, // needsFocus
            false, // setTop
            newBlock.id
          );
        } else if (operation === "update") {
          const targetIndex = editor.blocks.getBlockIndex(blockId);
          if (targetIndex > -1) {
            await editor.blocks.delete(targetIndex);
            await editor.blocks.insert(
              updatedBlock.type,
              updatedBlock.data,
              {},
              targetIndex,
              true, // needsFocus
              false, // setTop
              updatedBlock.id
            );
          }
        } else if (operation === "delete") {
          const targetIndex = editor.blocks.getBlockIndex(blockId);
          if (targetIndex > -1) await editor.blocks.delete(targetIndex);
        }

        // --- Important: Save history after *each* patch ---
        const updatedData = await editor.save();
        await saveToHistory(updatedData);

      } catch (error) {
        console.error("Failed to apply patch:", error, patch);
      }
    },
    [editor, isEditorReady, saveToHistory] // Dependencies
  );

  /**
   * WebSocket Message Handling Effect.
   * This effect sets up the `onmessage` listener for the WebSocket.
   * It routes incoming messages to the correct state handlers.
   */
  useEffect(() => {
    if (!socket || !editor) return;

    const handleMessage = async (event) => {
      const message = JSON.parse(event.data);
      switch (message.type) {

        case "ai_reasoning":
          addAgentActivityMessage(message.payload, 'reasoning');
          break;

        case "status":
          setAiStatus(message.payload.message);
          addAgentActivityMessage(message.payload, 'status');
          break;

        case "patch":
          await applyPatch(message.payload);
          break;

        case "clarification":
          isAiProcessing.current = false;
          setAiClarification(message.payload);
          setAiStatus("Awaiting Input");
          addAgentActivityMessage({
            message: `AI asks: "${message.payload.question}"`,
            stage: 'planning'
          }, 'status');

          // --- IMMUTABLE STATE UPDATE (FIXED) ---
          setConversationHistory((prev) =>
            prev.map((turn, index) =>
              index === prev.length - 1 // Is it the last turn?
                ? { ...turn, ai: `Asks: "${message.payload.question}"` } // Create new turn object
                : turn
            )
          );
          break;

        case "complete":
          isAiProcessing.current = false;
          const finalDocument = message.payload.finalDocument;

          await editor.render(finalDocument);
          await saveToHistory(finalDocument);

          setAiStatus("Ready");
          addAgentActivityMessage({ message: "Operation completed successfully.", stage: 'execution' }, 'status');

          // --- IMMUTABLE STATE UPDATE (FIXED) ---
          setConversationHistory((prev) =>
            prev.map((turn, index) =>
              index === prev.length - 1 // Is it the last turn?
                ? { ...turn, ai: "Operation completed.", documentStateAfter: finalDocument } // Create new turn object
                : turn
            )
          );
          break;

        case "error":
          isAiProcessing.current = false;
          const errorMessage = message.payload.message || "Unknown error";
          setAiStatus(`Error: ${errorMessage}`);
          addAgentActivityMessage({ message: `Error: ${errorMessage}`, stage: 'error' }, 'status');

          // --- IMMUTABLE STATE UPDATE (FIXED) ---
          setConversationHistory((prev) =>
            prev.map((turn, index) =>
              index === prev.length - 1 // Is it the last turn?
                ? { ...turn, ai: `Error: ${errorMessage}` } // Create new turn object
                : turn
            )
          );
          break;

        default:
          if (message.type !== 'keep_alive') {
            console.warn("Received unknown WebSocket message type:", message.type);
          }
      }
    };

    socket.onmessage = handleMessage;

    return () => {
      socket.onmessage = null; // Cleanup
    };

  }, [socket, editor, saveToHistory, applyPatch]);

  /**
   * Keep-Alive Effect.
   * Sends a ping every 3 seconds to keep the WebSocket connection alive.
   */
  useEffect(() => {
    if (!socket) return;
    const intervalId = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'keep_alive' }));
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [socket]);

  /**
   * Calculate and set header height for proper panel positioning.
   * This ensures the AI Assistant panel appears below the header.
   */
  useEffect(() => {
    let resizeObserver = null;
    let resizeObserverGlobal = null;
    let headerElement = null;
    let globalHeaderElement = null;
    let timeoutId = null;

    const calculateHeaderHeight = () => {
      // Find the editor header element
      headerElement = document.querySelector('.editor-header');
      if (headerElement) {
        // Use getBoundingClientRect to get the absolute position from top of viewport
        // This automatically includes all headers/space above it
        const rect = headerElement.getBoundingClientRect();
        const editorHeaderHeight = rect.height || headerElement.offsetHeight;
        // The top position tells us how much space is above the editor header
        // Add the header height to get the total height from top
        const totalHeight = rect.top + editorHeaderHeight;

        // Set CSS custom property for total header height
        document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
      } else {
        // Fallback: calculate from global header if editor header not found
        globalHeaderElement = document.querySelector('.header:not(.editor-header)');
        if (globalHeaderElement) {
          const globalRect = globalHeaderElement.getBoundingClientRect();
          const totalHeight = globalRect.top + globalRect.height;
          document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
        } else {
          // Final fallback to default value
          document.documentElement.style.setProperty('--header-height', '60px');
        }
      }
    };

    // Recalculate on window resize in case header height changes
    const handleResize = () => {
      // Use a small delay to ensure layout is complete
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateHeaderHeight, 100);
    };

    // Calculate immediately on mount (in case DOM is already ready)
    calculateHeaderHeight();

    // Also calculate after a small delay to ensure DOM is fully rendered
    timeoutId = setTimeout(() => {
      calculateHeaderHeight();

      // Set up ResizeObserver for more accurate detection of header size changes
      if (window.ResizeObserver) {
        // Observe editor header
        headerElement = document.querySelector('.editor-header');
        if (headerElement) {
          resizeObserver = new ResizeObserver(() => {
            calculateHeaderHeight();
          });
          resizeObserver.observe(headerElement);
        }

        // Observe global header if it exists
        globalHeaderElement = document.querySelector('.header:not(.editor-header)');
        if (globalHeaderElement) {
          resizeObserverGlobal = new ResizeObserver(() => {
            calculateHeaderHeight();
          });
          resizeObserverGlobal.observe(globalHeaderElement);
        }
      }
    }, 100);

    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && headerElement) {
        resizeObserver.unobserve(headerElement);
      }
      if (resizeObserverGlobal && globalHeaderElement) {
        resizeObserverGlobal.unobserve(globalHeaderElement);
      }
    };
  }, []);

  // --- AI Interaction Handlers ---

  /**
   * Submits the user's prompt (or clarification response) to the AI.
   * Resets state, prepares the payload, and sends the 'processRequest' message.
   * @param {Event} e - The form submission event.
   * @param {string | null} clarificationResponse - Optional text from a clarification.
   */
  const handlePromptSubmit = (e, clarificationResponse = null) => {
    e.preventDefault();
    if (!socket || !editor || !isEditorReady || isAiProcessing.current) return;

    const currentPrompt = clarificationResponse || prompt;
    if (!currentPrompt.trim()) return;

    // --- Reset State for New Request ---
    isAiProcessing.current = true;
    setAiStatus("Processing...");
    setAiClarification(null);
    setPrompt("");
    setAgentActivity([]); // Clear the previous activity log

    const performSubmit = async () => {
      try {
        const documentData = await editor.save();

        // Add this new request to the chat history
        const newTurn = {
          human: currentPrompt,
          ai: "Processing...", // Placeholder
          documentStateBefore: documentData,
        };
        // Use functional update to ensure we have the latest state
        setConversationHistory(prev => [...prev, newTurn]);

        // Filter history to send only *completed* turns to the backend
        // We use `conversationHistory` (the state *before* this submit)
        // to get the last stable history.
        const validConversationHistory = conversationHistory.filter(
          (turn) =>
            turn.ai &&
            turn.ai !== "Processing..." &&
            turn.documentStateBefore &&
            turn.documentStateAfter // Ensure the turn was fully completed
        );

        // Prepare request payload
        const requestPayload = {
          document: documentData,
          prompt: currentPrompt,
          conversationHistory: validConversationHistory,
          token: currentUser().token
        };

        // Send 'processRequest' message
        socket.send(createWebSocketMessage('processRequest', requestPayload));

      } catch (error) {
        console.error("Failed to save editor data before submit:", error);
        setAiStatus("Error: Could not save document.");
        isAiProcessing.current = false;
      }
    };

    performSubmit();
  };

  /**
   * Sends a 'stopRequest' message to the backend to cancel
   * the in-progress AI operation.
   */
  const handleStopGeneration = async () => {
    if (!socket || !editor) return;
    isAiProcessing.current = false;
    socket.send(createWebSocketMessage('stopRequest'));

    // Save the partially generated content
    const partialData = await editor.save();
    await saveToHistory(partialData);

    setAiStatus("Ready");
    setAiClarification(null);
    addAgentActivityMessage({ message: "Operation cancelled by user.", stage: 'execution' }, 'status');

    // --- IMMUTABLE STATE UPDATE (FIXED) ---
    setConversationHistory((prev) =>
      prev.map((turn, index) =>
        index === prev.length - 1 // Is it the last turn?
          ? { ...turn, ai: "Operation cancelled by user." } // Create new turn object
          : turn
      )
    );
  };

  // --- Document History Handlers ---

  /**
   * Moves one step back in the document history (Undo).
   */
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      editor.render(documentHistory[newIndex]);
    }
  };

  /**
   * Moves one step forward in the document history (Redo).
   */
  const handleRedo = () => {
    if (historyIndex < documentHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      editor.render(documentHistory[newIndex]);
    }
  };

  // --- Toolbar & Export Handlers ---

  /**
   * Inserts a new, empty block of a specified type at the current
   * caret position.
   * @param {string} blockType - The type of block (e.g., 'header', 'list').
   * @param {Object} blockData - The default data for the block.
   */
  const handleInsertBlock = useCallback(
    async (blockType, blockData) => {
      if (!editor || !isEditorReady || isAiProcessing.current) return;

      try {
        const currentBlockIndex = editor.blocks.getCurrentBlockIndex();
        const targetIndex = currentBlockIndex >= 0 ? currentBlockIndex + 1 : 0;
        await editor.blocks.insert(blockType, blockData, {}, targetIndex, true);
        editor.caret.setToBlock(targetIndex);

        const updatedData = await editor.save();
        await saveToHistory(updatedData);
      } catch (error) {
        console.error("Error inserting block:", error);
      }
    },
    [editor, isEditorReady, saveToHistory]
  );

  /**
   * Copies the entire document content as plain text to the clipboard.
   */
  const handleCopyText = async () => {
    if (!editor) return;
    const savedData = await editor.save();
    let textToCopy = "";

    savedData.blocks.forEach((block) => {
      switch (block.type) {
        case "header":
          textToCopy += `${block.data.text}\n\n`;
          break;
        case "paragraph":
          textToCopy += `${block.data.text}\n\n`;
          break;
        case "list":
          const listItems = block.data.items
            .map((item) => {
              const text = typeof item === 'object' ? item.content : item;
              return block.data.style === "ordered" ? `• ${text}` : `- ${text}`;
            })
            .join("\n");
          textToCopy += `${listItems}\n\n`;
          break;
        case "quote":
          textToCopy += `> ${block.data.text}\n- ${block.data.caption}\n\n`;
          break;
        case "checklist":
          const checklistItems = block.data.items
            .map((item) =>
              item.checked ? `[x] ${item.text}` : `[ ] ${item.text}`
            )
            .join("\n");
          textToCopy += `${checklistItems}\n\n`;
          break;
        case "code":
          textToCopy += `\`\`\`\n${block.data.code}\n\`\`\`\n\n`;
          break;
        default:
          textToCopy += "\n";
          break;
      }
    });

    try {
      await navigator.clipboard.writeText(textToCopy.trim());
      setCopyButtonText("Copied!");
      setTimeout(() => setCopyButtonText("Copy Text"), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
      setCopyButtonText("Error!");
      setTimeout(() => setCopyButtonText("Copy Text"), 2000);
    }
  };

  /**
   * Converts the EditorJS JSON data into a .docx file.
   * @param {Object} editorData - The EditorJS data object.
   * @returns {Document} A `docx` Document object.
   */
  const convertToDocx = async (editorData) => {
    const children = [];
    editorData.blocks.forEach((block) => {
      // Switch statement for block types...
      // (This logic remains unchanged from your provided code)
      switch (block.type) {
        case "header":
          const levelMap = {
            1: HeadingLevel.HEADING_1, 2: HeadingLevel.HEADING_2,
            3: HeadingLevel.HEADING_3, 4: HeadingLevel.HEADING_4,
            5: HeadingLevel.HEADING_5, 6: HeadingLevel.HEADING_6,
          };
          children.push(new Paragraph({
            children: [new TextRun(block.data.text || "")],
            heading: levelMap[block.data.level] || HeadingLevel.HEADING_1,
            spacing: { after: 200 },
          }));
          break;
        case "paragraph":
          children.push(new Paragraph({
            children: [new TextRun(block.data.text || "")],
            spacing: { after: 200 },
          }));
          break;
        case "list":
          if (block.data.items && Array.isArray(block.data.items)) {
            block.data.items.forEach((item, index) => {
              const text = typeof item === 'object' ? item.content : item;
              const prefix = block.data.style === "ordered" ? `${index + 1}. ` : "• ";
              children.push(new Paragraph({
                children: [new TextRun(prefix + (text || ""))],
                spacing: { after: 100 },
                indent: { left: 720 },
              }));
            });
            children.push(new Paragraph({ children: [new TextRun("")] })); // Spacer
          }
          break;
        // ... (other cases: quote, checklist, code, default)
      }
    });

    if (children.length === 0) {
      children.push(new Paragraph({ children: [new TextRun("This document is empty.")] }));
    }

    return new Document({
      sections: [{ properties: {}, children: children }],
    });
  };

  /**
   * Triggers the DOCX download.
   */
  const handleDownloadDocx = async () => {
    if (!editor) return;
    try {
      const editorData = await editor.save();
      if (!editorData || !editorData.blocks) throw new Error("No content to export");

      const doc = await convertToDocx(editorData);
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      const filename = `document_${dateStr}.docx`;
      const blob = await Packer.toBlob(doc);
      saveAs(blob, filename);

    } catch (error) {
      console.error("Error generating DOCX:", error);
      alert(`Error generating DOCX file: ${error.message}.`);
    }
  };

  // --- File Save/Download Handlers ---

  /**
   * Helper function that wraps the imported uploadManager, 
   * injecting the current user.
   * @param {Object} params - Upload parameters { files, parentId }
   */
  const upload = (params) => {
    if (!currentUser) {
      console.error("Cannot upload: user is not defined.");
      return;
    }
    uploadManager.fileUpload({
      ...params,
      user: currentUser()
    });
  };

  /**
   * Saves the current editor content back to the server.
   */
  const onEditorJsSave = async () => {
    if (!editor) return;
    try {
      const outputData = await editor.save();
      const jsonString = JSON.stringify(outputData, null, 2);
      const now = new Date();

      let fileName = `document_${now.toISOString()}.editorjs`;
      if (fileData?.file?.Name) {
        fileName = fileData.file.Name;
      }

      const file = new File([jsonString], fileName, {
        type: EDITOR_JS_MIME_TYPE,
        lastModified: now.getTime()
      });

      upload({
        files: [file],
        parentId: fileData?.file?.ParentId || 'documents', // Use root 'documents' as fallback
      });

    } catch (error) {
      console.error("Failed to save editor data:", error);
    }
  };

  /**
   * Downloads the current editor content as a local .editorjs JSON file.
   */
  const onEditorJsDownloadSave = async () => {
    if (!editor) return;
    try {
      const outputData = await editor.save();
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];

      let filename = `editor_data_${dateStr}.editorjs`;
      if (fileData?.file?.Name) {
        filename = fileData.file.Name;
      }

      const jsonString = JSON.stringify(outputData, null, 2);
      const blob = new Blob([jsonString], { type: EDITOR_JS_MIME_TYPE });
      saveAs(blob, filename);

    } catch (error) {
      console.error("Failed to download editor data:", error);
    }
  };

  // --- Grounding Modal Handlers ---

  /**
   * Fetches the list of available grounding folders from the API.
   */
  const fetchGroundingFolders = async () => {
    setLoadingGroundingFolders(true);
    try {
      const data = await api.sendMessage("dms/getGroundingFolders", {
        user: currentUser(),
        body: {}
      });

      if (data.success && data.folders) {
        setGroundingFolders(data.folders);
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching grounding folders:', error);
      alert('Failed to load grounding folders. Please try again.');
    } finally {
      setLoadingGroundingFolders(false);
    }
  };

  const handleOpenGroundingModal = () => {
    setShowGroundingModal(true);
    fetchGroundingFolders();
  };

  const handleCloseGroundingModal = () => {
    setShowGroundingModal(false);
    setGroundingFolders([]);
  };

  const handleGroundingFolderToggle = (folderId) => {
    setSelectedGroundingIds(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const handleApplyGroundingSelection = () => {
    console.log('Applied grounding folders:', selectedGroundingIds);
    setShowGroundingModal(false);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  // --- JSX Render ---

  // Main container class list, reacts to chat state
  const mainContainerClasses = [
    "editor-main-container",
    isChatMaximized ? "chat-maximized" : ""
  ].join(" ");

  // AI Panel class list, reacts to chat state
  const chatWrapperClasses = [
    "prompt-container-wrapper",
    showChat ? "visible" : "",
    isChatMaximized ? "maximized" : ""
  ].join(" ");


  return (
    <div className="editor-page">
      {/* Header with main toolbar */}
      <header className="editor-header">
        <div className="container-fluid pb-0 pt-0">
          <div className="row align-items-center justify-content-center">
            <div className="col">
              <div className="d-flex align-items-center justify-content-center w-100">
                <EditorToolbar
                  {...{
                    fontFamily, setFontFamily, fontSize, setFontSize,
                    handleInsertBlock, onEditorJsSave, onEditorJsDownloadSave,
                    isAiProcessing: isAiProcessing.current,
                    aiStatus, showChat, setShowChat, handleCopyText,
                    handleDownloadDocx, handleUndo, handleRedo,
                    canUndo: historyIndex > 0,
                    canRedo: historyIndex < documentHistory.length - 1,
                    copyButtonText
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className={mainContainerClasses}>
        {/* Editor Area */}
        <div className={`editor-main ${showChat ? "with-chat" : ""}`}>
          {/* Document Name */}
          {fileData?.file?.Name && (
            <div className="document-name-header" style={{
              padding: '10px 15px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              fontSize: '18px',
              fontWeight: '500',
              color: '#333',
              textAlign: 'center'
            }}>
              {fileData.file.Name}
            </div>
          )}

          <div className={`editor-container ${showChat ? "with-chat" : ""}`}>
            <div
              ref={editorHolderRef}
              className="editor-js-container"
              style={{
                fontFamily: fontFamily,
                fontSize: fontSize + "px",
              }}
            ></div>
          </div>
        </div>

        {/* AI Chat Panel (Sidebar) */}
        <div className={chatWrapperClasses}>
          <div className="prompt-container">

            {/* --- REFACTORED PANEL HEADER --- */}
            <div className="prompt-header">
              <div className="prompt-title-bar">
                <h3>AI Assistant</h3>
                <div className="prompt-header-controls">
                  <button
                    className="btn btn-icon btn-sm"
                    onClick={() => setIsChatMaximized(!isChatMaximized)}
                    title={isChatMaximized ? "Minimize" : "Maximize"}
                  >
                    <i className={`bi ${isChatMaximized ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-close-panel"
                    onClick={() => setShowChat(false)}
                    title="Close AI Assistant Panel"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
              <div className="prompt-actions-bar">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleOpenGroundingModal}
                  title="Configure Grounding Folders"
                  disabled={isAiProcessing.current}
                >
                  <i className="bi bi-folder me-1"></i>
                  Grounding
                  {selectedGroundingIds.length > 0 && (
                    <span className="badge bg-primary ms-1">{selectedGroundingIds.length}</span>
                  )}
                </button>
              </div>
              <div className="prompt-status-wrapper">
                <span
                  className={`badge ${aiStatus.includes("Error") || aiStatus.includes("Disconnected")
                      ? "bg-danger"
                      : aiStatus === "Ready" || aiStatus === "Awaiting Input"
                        ? "bg-success"
                        : "bg-primary"
                    }`}
                >
                  {aiStatus}
                </span>
              </div>
            </div>

            {/* --- NEW SCROLLABLE BODY --- */}
            <div className="prompt-body">
              {/* Agent Activity Log */}
              {isAiProcessing.current && agentActivity.length > 0 && (
                <div className="agent-activity-log">
                  <div className="list-group list-group-flush">
                    {agentActivity.map((activity, index) => (
                      <div key={index} className="list-group-item activity-item">
                        {activity.type === 'reasoning' ? (
                          <>
                            <i className="bi bi-robot text-primary me-2" title="AI thought"></i>
                            <span className="text-muted fst-italic">{activity.message}</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-info-circle text-info me-2" title={`Stage: ${activity.stage}`}></i>
                            <span>{activity.message}</span>
                            {activity.detail && <small className="d-block text-muted">{activity.detail}</small>}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading Spinner */}
              {isAiProcessing.current && (
                <div className="processing-box text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <button
                    className="btn btn-outline-danger w-100 mt-3"
                    onClick={handleStopGeneration}
                  >
                    <i className="bi bi-stop-circle me-1"></i>
                    Stop Generating
                  </button>
                </div>
              )}

              {/* Clarification Box */}
              {!isAiProcessing.current && aiClarification && (
                <div className="clarification-box">
                  <h6>The assistant asks:</h6>
                  <p>{aiClarification.question}</p>
                  {aiClarification.options && aiClarification.options.length > 0 ? (
                    aiClarification.options.map((option) => (
                      <button
                        key={option}
                        className="btn btn-outline-primary btn-sm suggestion-chip"
                        onClick={(e) => handlePromptSubmit(e, option)}
                      >
                        {option}
                      </button>
                    ))
                  ) : (
                    <form onSubmit={handlePromptSubmit}>
                      <textarea
                        className="form-control prompt-textarea"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Your answer..."
                        rows="4"
                        autoFocus
                      ></textarea>
                      <button
                        type="submit"
                        className="btn btn-primary w-100 mt-2"
                        disabled={!prompt.trim()}
                      >
                        Send Answer
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* Main Prompt Input */}
              {!isAiProcessing.current && !aiClarification && (
                <form onSubmit={handlePromptSubmit} className="prompt-form">
                  <div className="mb-3">
                    <label htmlFor="promptInput" className="form-label">
                      Describe what you want to create or modify:
                    </label>
                    <textarea
                      id="promptInput"
                      className="form-control prompt-textarea"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Create an article from scratch or ask to modify the content..."
                      rows="6"
                      disabled={!socket || aiStatus === "Connecting..." || aiStatus.includes("Error")}
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="btn w-100"
                    style={{ backgroundColor: "#34f57b", color: "white" }}
                    disabled={
                      !socket ||
                      aiStatus === "Connecting..." ||
                      aiStatus.includes("Error") ||
                      !prompt.trim()
                    }
                  >
                    <i className="bi bi-magic me-1"></i>
                    Generate Content
                  </button>
                </form>
              )}

              {/* Conversation History */}
              {conversationHistory.length > 0 && (
                <div className="conversation-history mt-3">
                  <h6>Recent Activity:</h6>
                  <div className="conversation-list">
                    {[...conversationHistory]
                      .reverse()
                      .map((entry, index) => (
                        <div key={index} className="conversation-entry">
                          <div className="user-message">
                            <strong>You:</strong> {entry.human}
                          </div>
                          <div className="ai-message mt-1">
                            <strong>AI:</strong>
                            <span
                              className={`ms-1 ${entry.ai === "Processing..." ? "text-warning"
                                  : entry.ai.includes("Error") ? "text-danger"
                                    : entry.ai.includes("Asks:") ? "text-info"
                                      : entry.ai === "Operation completed." ? "text-success"
                                        : "text-muted"
                                }`}
                            >
                              {entry.ai}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div> {/* End .prompt-body */}
          </div>
        </div>
      </div>

      {/* Grounding Modal */}
      {showGroundingModal && (
        <div
          className="modal fade show"
          style={{ display: 'block' }}
          tabIndex="-1"
          onClick={handleCloseGroundingModal}
        >
          <div className="modal-backdrop fade show"></div>
          <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              {/* Modal Header */}
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-folder me-2"></i>
                  Select Grounding Folders
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseGroundingModal}
                ></button>
              </div>
              {/* Modal Body */}
              <div className="modal-body">
                {loadingGroundingFolders ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : groundingFolders.length === 0 ? (
                  <div className="text-center text-muted">
                    <p>No grounding folders available.</p>
                  </div>
                ) : (
                  <div className="grounding-folders-list">
                    <p className="text-muted mb-3">
                      Select one or more folders to use for grounding.
                    </p>
                    {groundingFolders.map(folder => (
                      <div key={folder.Id} className="form-check grounding-folder-item">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`folder-${folder.Id}`}
                          checked={selectedGroundingIds.includes(folder.Id)}
                          onChange={() => handleGroundingFolderToggle(folder.Id)}
                        />
                        <label className="form-check-label w-100" htmlFor={`folder-${folder.Id}`}>
                          <div className="folder-info">
                            <div className="folder-name fw-bold">{folder.Name}</div>
                            <div className="folder-date text-muted small">
                              Last updated: {formatDate(folder.ItemLastUpdated)}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Modal Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseGroundingModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApplyGroundingSelection}
                  disabled={loadingGroundingFolders}
                >
                  Apply Selection ({selectedGroundingIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorPage;